<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'

const { t } = useI18n()

const steps = [
  {
    number: 1,
    text: t('widget.card_instructions.bakai.step1')
  },
  {
    number: 2,
    text: t('widget.card_instructions.bakai.step2')
  },
  { number: 3, text: t('widget.card_instructions.bakai.step3') },
  {
    number: 4,
    text: t('widget.card_instructions.bakai.step4')
  },
  {
    number: 5,
    text: t('widget.card_instructions.bakai.step5')
  }
]

const images = [
  '/images/banks/card_order_manuals/bakai/bakai_1.png',
  '/images/banks/card_order_manuals/bakai/bakai_2.webp',
  '/images/banks/card_order_manuals/bakai/bakai_3.webp',
  '/images/banks/card_order_manuals/bakai/bakai_4.webp',
  '/images/banks/card_order_manuals/bakai/bakai_5.webp'
]

const appLink = 'https://onelink.to/6f3nqy'

const modules = [Autoplay]
const activeSlide = ref(0)
let swiperInstance: SwiperType | null = null

const goToSlide = (index: number) => {
  if (swiperInstance) {
    swiperInstance.slideToLoop(index)
  }
}

const onSwiper = (swiper: SwiperType) => {
  swiperInstance = swiper
}

const onSlideChange = (swiper: SwiperType) => {
  activeSlide.value = swiper.realIndex
}
</script>

<template>
  <ContentBlock variant="white" class="overflow-hidden px-2 sm:px-4">
    <div class="rounded-lg">
      <div class="relative flex flex-col items-center gap-6 lg:flex-row lg:items-start">
        <div class="flex w-full flex-shrink-0 flex-col items-center gap-4 lg:w-auto">
          <div
            class="mx-auto h-80 w-full max-w-xs overflow-hidden rounded-lg bg-gray-100 p-2 sm:h-80 sm:w-80 sm:max-w-none sm:p-6 md:h-96 md:w-96 md:p-8"
          >
            <Swiper
              class="h-full w-full"
              :modules="modules"
              :autoplay="{ delay: 4000, disableOnInteraction: false }"
              :loop="true"
              @swiper="onSwiper"
              @slide-change="onSlideChange"
            >
              <SwiperSlide v-for="(image, index) in images" :key="index">
                <NuxtImg
                  :src="image"
                  :alt="$t('widget.card_instructions.bakai.alt_text', { index: index + 1 })"
                  class="h-full w-full object-contain"
                  loading="lazy"
                />
              </SwiperSlide>
            </Swiper>
          </div>
          <p class="mb-0 text-center font-bold text-gray-600">
            {{ $t('widget.card_instructions.bakai.download_app') }}
          </p>
          <div class="flex w-full max-w-full gap-2">
            <NuxtLinkLocale
              :to="appLink"
              class="flex flex-1 flex-col items-end"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="[&>svg]:w-full [&>svg]:max-w-full"><UiIcon name="app-store" /></i>
            </NuxtLinkLocale>

            <NuxtLinkLocale
              :to="appLink"
              class="flex flex-1 flex-col items-start"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i class="[&>svg]:w-full [&>svg]:max-w-full"><UiIcon name="google-play" /></i>
            </NuxtLinkLocale>
          </div>
        </div>

        <!-- Steps Content -->
        <div class="w-full max-w-full flex-1 px-2 sm:px-0 lg:max-w-2xl">
          <div class="mb-6">
            <Heading class="mb-2" tag="h3">{{
              $t('widget.card_instructions.bakai.heading')
            }}</Heading>
            <p>{{ $t('widget.card_instructions.bakai.subheading') }}</p>
          </div>

          <!-- Steps List -->
          <ol class="space-y-6">
            <li
              v-for="(step, index) in steps"
              :key="step.number"
              class="flex cursor-pointer items-center gap-4 transition-colors duration-200"
              @click="goToSlide(index)"
            >
              <div class="flex-shrink-0">
                <span
                  class="flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-all duration-200"
                  :class="
                    activeSlide === index
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-blue-300'
                  "
                >
                  {{ step.number }}
                </span>
              </div>

              <div class="flex-1">
                <p
                  class="text-base leading-relaxed transition-colors duration-200"
                  :class="
                    activeSlide === index
                      ? 'font-medium text-gray-900'
                      : 'text-gray-700 hover:text-gray-900'
                  "
                >
                  {{ step.text }}
                </p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>
  </ContentBlock>
</template>
